// react
import { ReactNode, useContext } from 'react';

// types
import { ActionButton, Entity, User } from '../../../@types';
import { entityTypesReadOnly } from '../../../@types/unions';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUser } from '../../../App.context';

// material
import { Box, BoxProps, Button, Grid, SxProps, Typography, TypographyProps } from '@mui/material';

// ui
import { IconCircle, IconCircleProps } from '../IconCircle/IconCircle';
import { Loading, LoadingProps } from '../Loading/Loading';

// props
export const emptyPositions = ['absolute', 'relative'];
export const emptyVariants = ['container', 'page'];

export type EmptyPosition = (typeof emptyPositions)[number];
export type EmptyVariant = (typeof emptyVariants)[number];

export interface EmptyProps extends Omit<BoxProps, 'content' | 'position' | 'title'> {
	actions?: ActionButton[];
	content?: TypographyProps;
	hasContent?: boolean;
	hasTitle?: boolean;
	IconCircleProps?: IconCircleProps;
	isLoading?: boolean;
	LoadingProps?: LoadingProps;
	position?: EmptyPosition;
	sxBox?: SxProps;
	title?: TypographyProps;
	variant?: EmptyVariant;
}

export const Empty = ({
	actions = [],
	content,
	hasContent = true,
	hasTitle = true,
	IconCircleProps,
	isLoading,
	LoadingProps,
	position = 'relative',
	sxBox = {},
	title,
	variant = 'container',
	...props
}: EmptyProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { user } = useContext(ContextUser);
	const userEntities = user?.entities as Entity[];

	// vars
	const iconCircleSize = IconCircleProps?.size || 'lg';
	const userRolesReadOnly = ['STAFF'];
	const hasActions = actions.length > 0;
	const isUserRolePermitted = !userRolesReadOnly.includes((user as User)?.role);
	const isEntityTypePermitted = !entityTypesReadOnly.some((entityType) =>
		Array.isArray(userEntities)
			? userEntities.some((entity) => {
					if (typeof entity === 'object') {
						return entity.type === entityType;
					}
					return false;
				})
			: false
	);

	let icon: ReactNode = (
		<IconCircle
			{...IconCircleProps}
			size={iconCircleSize}
			type={IconCircleProps?.type || 'search'}
		/>
	);

	if (isLoading) {
		icon = <Loading {...LoadingProps} />;
	}

	return (
		<Box
			sx={(theme) => {
				let sx: SxProps = {
					...(sxBox as object),
				};

				if (position === 'absolute') {
					sx = {
						...sx,
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
					};
				}

				if (variant === 'page') {
					sx = {
						...sx,
						height: `calc(100vh - ${theme.variables?.headerHeight})`,
					};
				}

				return sx;
			}}
			{...props}>
			<Grid
				alignItems="center"
				container
				direction="column"
				justifyContent="center"
				spacing={['md', 'sm'].includes(iconCircleSize) ? 1 : 2}
				sx={{ height: '100%', marginTop: 0, textAlign: 'center' }}>
				<Grid item sx={{ display: 'flex', paddingTop: `0 !important` }}>
					{icon}
				</Grid>
				{(hasContent || hasTitle) && (
					<Grid item>
						<Box sx={{ maxWidth: 260 }}>
							{hasTitle && (
								<Typography
									variant="h3"
									{...title}
									children={isLoading ? t('loading') : title?.children || t('empty.default.title')}
								/>
							)}
							{hasContent && (
								<Typography
									{...content}
									children={
										isLoading
											? t('empty.loading.content')
											: content?.children || t('empty.default.content')
									}
								/>
							)}
						</Box>
					</Grid>
				)}

				{!isLoading && hasActions && (isUserRolePermitted || isEntityTypePermitted) && (
					<Grid item>
						<Grid container spacing={2}>
							{actions.map((action, i) => {
								const { children, ...actionProps } = action;
								return (
									<Grid item key={`empty-action-button-${i}`}>
										<Button {...actionProps}>{children}</Button>
									</Grid>
								);
							})}
						</Grid>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};
